"use client"
import styled from "@emotion/styled"
import { Box, Typography } from "@mui/material"
import React, { isValidElement, useMemo, useState } from "react"
import { colors, sizes } from "../theme/theme"
import { renderChildWithItems } from "./componentUtils"
import FilteredItemWrapper from "./FilteredItemWrapper"
import FiltersBar from "./FiltersBar"
import {
  getInitialCategoryData,
  getValidCategories,
  processItemsData,
} from "./filtersHelpers"
import { FiltersWrapperProps } from "./types"

const StyledFiltersWrapper = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`

const CategorySection = styled(Box)`
  display: flex;
  flex-direction: column;
  align-items: start;
  justify-content: flex-start;
  width: 100%;
  gap: ${sizes.baseTriple};
`

const CategoryTitle = styled(Typography)`
  color: ${colors.black};
  font-weight: 700;
`

export default function FiltersWrapper({
  categories,
  itemsList = [],
  searchBarText,
  children,
  style,
  className,
  groupByCategory = false,
  allFilterCategory,
  limit,
}: FiltersWrapperProps) {
  // ========== MEMOIZED CALCULATIONS ==========
  // Calculate valid categories (filters out empty/falsy values)
  const validCategories = useMemo(
    () => getValidCategories(categories),
    [categories],
  )

  // Determine initial category selection
  const initialCategoryData = useMemo(
    () => getInitialCategoryData(validCategories),
    [validCategories],
  )

  // ========== STATE MANAGEMENT ==========
  const [value, setValue] = useState<number | null>(initialCategoryData.value)
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    initialCategoryData.selectedCategory,
  )
  const [searchTerm, setSearchTerm] = useState<string>("")
  const [isSearchOpen, setIsSearchOpen] = useState<boolean>(false)

  // ========== DATA PROCESSING ==========
  // Process and filter items based on current state
  const { filteredItems, groupedItems } = useMemo(() => {
    return processItemsData(
      itemsList,
      searchTerm,
      selectedCategory,
      allFilterCategory,
      validCategories,
      groupByCategory,
    )
  }, [
    itemsList,
    searchTerm,
    selectedCategory,
    allFilterCategory,
    validCategories,
    groupByCategory,
  ])

  // ========== EVENT HANDLERS ==========
  const handleCategoryChange = (
    event: React.SyntheticEvent,
    newValue: number,
  ) => {
    if (newValue === value) {
      return
    }
    setValue(newValue)
    setSelectedCategory(validCategories[newValue])
  }

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value)
  }

  const handleSearchToggle = (isOpen: boolean) => {
    setIsSearchOpen(isOpen)
    // Clear search term when closing the search
    if (!isOpen && searchTerm) {
      setSearchTerm("")
    }
  }

  // ========== RENDERING HELPERS ==========
  const shouldShowFiltersBar = validCategories.length > 1
  const shouldShowGroupedContent =
    (!selectedCategory || selectedCategory === allFilterCategory) &&
    groupByCategory &&
    Object.keys(groupedItems).length > 0

  const getItemsForStandardRendering = () => {
    return limit ? filteredItems.slice(0, limit) : filteredItems
  }

  const renderGroupedContent = () => {
    return Object.entries(groupedItems).map(([category, items]) => {
      if (category === allFilterCategory) return null

      return (
        <React.Fragment key={`category-${category}`}>
          <CategorySection>
            <CategoryTitle variant="h3">{category}</CategoryTitle>
            {React.Children.map(children, (child, index) => {
              if (!isValidElement(child)) return null

              return (
                <FilteredItemWrapper key={`filtered-item-${category}-${index}`}>
                  {renderChildWithItems(child, items)}
                </FilteredItemWrapper>
              )
            })}
          </CategorySection>
        </React.Fragment>
      )
    })
  }

  const renderStandardContent = () => {
    const itemsToRender = getItemsForStandardRendering()

    return React.Children.map(children, (child, index) => {
      // Safely return the child if not a valid element
      if (!isValidElement(child)) {
        return (
          <FilteredItemWrapper key={`filtered-item-${index}`}>
            {child}
          </FilteredItemWrapper>
        )
      }

      return (
        <FilteredItemWrapper key={`filtered-item-${index}`}>
          {renderChildWithItems(child, itemsToRender)}
        </FilteredItemWrapper>
      )
    })
  }

  // ========== MAIN RENDER ==========
  return (
    <StyledFiltersWrapper style={style} className={className}>
      {shouldShowFiltersBar && (
        <FiltersBar
          value={value}
          handleChange={handleCategoryChange}
          categories={validCategories}
          searchBarText={searchBarText}
          searchTerm={searchTerm}
          onSearchChange={handleSearchChange}
          isSearchOpen={isSearchOpen}
          onSearchToggle={handleSearchToggle}
        />
      )}

      {shouldShowGroupedContent
        ? renderGroupedContent()
        : renderStandardContent()}
    </StyledFiltersWrapper>
  )
}
